import { Context, ElementRect, ElementSize } from '@vpap_svelte/core';
import { extract } from '@vpap_svelte/internal';
/**
 * Reactively keep track of the dimensions of the chart.
 */
export class ChartDimensions {
    #chartEl = $state();
    #containerEl = $state();
    #chartRect = $derived(new ElementRect(() => this.#chartEl, { resizeOptions: { box: 'border-box' } }));
    #containerRect = $derived(new ElementRect(() => this.#containerEl, { resizeOptions: { box: 'border-box' } }));
    #chartDpiSize = $derived(new ElementSize(() => this.#chartEl, { box: 'device-pixel-content-box' }));
    #padding = $derived.by(() => {
        // return undefined if the divs are undefined
        // or if they haven't been updated yet with their dimensions
        if (this.#containerEl === undefined ||
            this.#chartEl === undefined ||
            this.#chartRect.isInitial ||
            this.#containerRect.isInitial) {
            return undefined;
        }
        return {
            left: this.#chartRect.left - this.#containerRect.left,
            right: this.#containerRect.right - this.#chartRect.right,
            top: this.#chartRect.top - this.#containerRect.top,
            bottom: this.#containerRect.bottom - this.#chartRect.bottom,
        };
    });
    constructor(container, chart) {
        const containerEl = $derived(extract(container));
        this.#containerEl = containerEl;
        const chartEl = $derived(extract(chart));
        this.#chartEl = chartEl;
        $effect(() => {
            this.#containerEl = containerEl;
        });
        $effect(() => {
            this.#chartEl = chartEl;
        });
    }
    /**
     * Get the actual element forming the container
     */
    get containerElement() {
        return this.#containerEl;
    }
    /**
     * Get the actual element forming the chart
     */
    get chartElement() {
        return this.#chartEl;
    }
    /**
     * A rectangle showing the dimensions of the outside chart container
     */
    get container() {
        return this.#containerRect;
    }
    /**
     * A rectangle showing the dimensions of the chart itself (ie what you'd put the scatterplot,etc in)
     */
    get chart() {
        return this.#chartRect;
    }
    /**
     * Returns a size box with the width and height of the chart in actual pixels
     * rather than CSS pixels. This is primarily used to automatically scale the `width` and `height`
     * of `canvas` elements but theoretically can have other purposes
     */
    get chartDpiDimensions() {
        return this.#chartDpiSize;
    }
    /**
     * The padding (in between the container and the chart)
     */
    get padding() {
        return this.#padding;
    }
}
/**
 * Context for the chart that can be used inside of the chart.
 */
export const ChartContext = new Context('ChartContext');
